// src/pages/TopupReturn.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient.js";
import { useTranslation } from "react-i18next";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const rupiah = (n) =>
  new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    maximumFractionDigits: 0,
  }).format(Number(n || 0));

const TopupReturn = () => {
  const { t } = useTranslation();
  const q = useQuery();
  const navigate = useNavigate();

  const merchantOrderId =
    q.get("merchantOrderId") || q.get("merchantorderid") || "";
  const duitkuRef = q.get("reference") || q.get("ref") || q.get("trx_id") || "";

  const [status, setStatus] = useState("checking"); // "checking" | "pending" | "paid" | "failed"
  const [tx, setTx] = useState(null);
  const [err, setErr] = useState("");
  const stopRef = useRef(false);

  useEffect(() => {
    let timer;
    let tries = 0;

    const poll = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session) {
          setErr(t("topupReturn.sessionExpired"));
          setStatus("failed");
          return;
        }

        const { data, error } = await supabase
          .from("transactions")
          .select("*")
          .eq("merchant_order_id", merchantOrderId)
          .limit(1)
          .maybeSingle();

        if (error) {
          console.error(error);
          setErr(t("topupReturn.checkFailed"));
          setStatus("failed");
          return;
        }

        if (!data) {
          setStatus("pending");
        } else {
          setTx(data);
          if (data.status === "paid") {
            setStatus("paid");
            stopRef.current = true;
            return;
          }
          if (data.status === "failed" || data.status === "canceled") {
            setStatus("failed");
            stopRef.current = true;
            return;
          }
          setStatus("pending");
        }

        tries += 1;
        if (tries >= 40) {
          // ~2 menit (40 x 3s)
          stopRef.current = true;
          return;
        }
      } catch (e) {
        console.error(e);
        setErr(t("topupReturn.errorOccurred"));
        stopRef.current = true;
      }
    };

    if (merchantOrderId) {
      poll();
      timer = window.setInterval(() => {
        if (!stopRef.current) poll();
      }, 3000);
    } else {
      setErr(t("topupReturn.orderIdNotFound"));
      setStatus("failed");
    }

    return () => {
      stopRef.current = true;
      if (timer) clearInterval(timer);
    };
  }, [merchantOrderId, t]);

  const statusText = {
    checking: {
      title: t("topupReturn.status.checking.title"),
      subtitle: t("topupReturn.status.checking.subtitle"),
    },
    pending: {
      title: t("topupReturn.status.pending.title"),
      subtitle: t("topupReturn.status.pending.subtitle"),
    },
    paid: {
      title: t("topupReturn.status.paid.title"),
      subtitle: t("topupReturn.status.paid.subtitle"),
    },
    failed: {
      title: t("topupReturn.status.failed.title"),
      subtitle: err || t("topupReturn.status.failed.subtitle"),
    },
  }[status];

  const amountPaid = tx?.amount_paid ? rupiah(tx.amount_paid) : null;
  const creditReceived =
    tx?.credit_received != null
      ? tx.credit_received.toLocaleString("id-ID")
      : null;

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-3xl">
        {/* Header */}
        <div className="mb-6 text-center">
          <p className="text-xs font-semibold tracking-[0.25em] uppercase text-blue-400">
            {t("topupReturn.header")}
          </p>
          <h1 className="mt-2 text-2xl sm:text-3xl font-bold text-slate-900">
            {t("topupReturn.pageTitle")}
          </h1>
        </div>

        {/* Card utama */}
        <div className="bg-white border border-blue-100 rounded-2xl shadow-lg shadow-blue-100/60 p-5 sm:p-7">
          {/* Judul status */}
          <div className="mb-4 sm:mb-5">
            <p
              className={`text-base sm:text-lg font-semibold ${status === "paid"
                  ? "text-blue-700"
                  : status === "failed"
                    ? "text-rose-600"
                    : "text-slate-800"
                }`}
            >
              {statusText?.title}
            </p>
            <p className="mt-1 text-xs sm:text-sm text-slate-500">
              {statusText?.subtitle}
            </p>
          </div>

          {/* Skeleton untuk checking */}
          {status === "checking" && (
            <div className="mb-5 flex items-center gap-3 text-sm text-slate-600">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
              <span>{t("topupReturn.loading")}</span>
            </div>
          )}

          {/* Dua kolom: detail & ringkasan */}
          <div className="grid gap-4 sm:grid-cols-2">
            {/* Detail transaksi */}
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs sm:text-sm text-slate-700 space-y-1.5">
              <p className="font-semibold text-slate-900">{t("topupReturn.transactionDetail")}</p>
              <div className="flex justify-between gap-2">
                <span className="text-slate-500">{t("topupReturn.merchantOrderId")}</span>
                <span className="font-mono text-[11px] sm:text-xs text-slate-900 truncate max-w-[11rem] sm:max-w-[14rem] text-right">
                  {merchantOrderId || "-"}
                </span>
              </div>
              {duitkuRef && (
                <div className="flex justify-between gap-2">
                  <span className="text-slate-500">{t("topupReturn.duitkuRef")}</span>
                  <span className="font-mono text-[11px] sm:text-xs text-slate-900 truncate max-w-[11rem] sm:max-w-[14rem] text-right">
                    {duitkuRef}
                  </span>
                </div>
              )}
            </div>

            {/* Ringkasan topup */}
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs sm:text-sm text-slate-700 space-y-1.5">
              <p className="font-semibold text-slate-900">{t("topupReturn.summary")}</p>
              <div className="flex justify-between gap-2">
                <span className="text-slate-500">{t("topupReturn.amountPaid")}</span>
                <span className="font-semibold text-slate-900">
                  {amountPaid || "-"}
                </span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-slate-500">{t("topupReturn.creditReceived")}</span>
                <span className="font-semibold text-slate-900">
                  {creditReceived ? `${creditReceived} ${t("common.credits").toLowerCase()}` : "-"}
                </span>
              </div>
              {status === "paid" && (
                <p className="text-[11px] text-blue-600 mt-1">
                  {t("topupReturn.creditSuccess")}
                </p>
              )}
            </div>
          </div>

          {/* Tombol aksi */}
          <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
            <button
              className="w-full sm:w-auto inline-flex justify-center items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-200 bg-white text-xs sm:text-sm text-slate-700 hover:bg-slate-50 transition-colors"
              onClick={() => navigate("/dashboard")}
            >
              {t("topupReturn.toDashboard")}
            </button>
            <div className="flex w-full sm:w-auto gap-2">
              <button
                className="flex-1 inline-flex justify-center items-center px-4 py-2.5 rounded-lg border border-slate-200 bg-white text-xs sm:text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                onClick={() => window.location.reload()}
              >
                {t("topupReturn.refreshStatus")}
              </button>
              <button
                className="flex-1 inline-flex justify-center items-center px-4 py-2.5 rounded-lg bg-blue-600 text-xs sm:text-sm font-semibold text-white hover:bg-blue-700 transition-colors"
                onClick={() => navigate("/dashboard/topup")}
              >
                {t("topupReturn.topupAgain")}
              </button>
            </div>
          </div>

          {status === "failed" && (
            <p className="mt-3 text-[11px] text-rose-500">
              {t("topupReturn.contactAdmin")}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default TopupReturn;
